import { yupResolver } from '@hookform/resolvers/yup'
import { t } from '@if/core/i18n'
import { lazyLoad } from '@if/core/utils'
import { dateOnly, InferType, mixed, number, object, ref } from '@if/core/yup'
import {
  EBankFailureCode,
  EOperationStatus,
  getEBankFailureCodeList,
  getEOperationStatusList,
} from '@src/types/constants'
import { CalendarSimulation } from '@src/types/loan/loan-simulation'
import { useMemo } from 'react'
import { Resolver } from 'react-hook-form'
import { getAvailablePaymentStatuses } from './LoanCalendarPaymentStatus-selectors'

export const getStatusThatNeedsTransactionDate = lazyLoad(
  () => new Set<EOperationStatus>([EOperationStatus.Complete, EOperationStatus.Failed]),
)

export const getChangePaymentStatusSchema = lazyLoad(() =>
  object({
    newStatus: mixed<EOperationStatus>()
      .oneOf(getEOperationStatusList())
      .required()
      .test(
        'test-isNotSameStatus',
        () => t('loan.payment.changeStatusPayment.errors.sameStatus'),
        (value, context) => {
          const currentStatus = context.resolve<EOperationStatus>(ref('$currentStatus'))

          if (!currentStatus || !value) return true
          return (value as EOperationStatus) !== currentStatus
        },
      )
      .test(
        'test-isValidStatusTransition',
        () => t('loan.payment.changeStatusPayment.errors.invalidNewStatus'),
        (value, context) => {
          if (!value) return true
          const currentStatus = context.resolve<EOperationStatus>(ref('$currentStatus'))
          if (!currentStatus) return true

          const validPaymentStatus = getAvailablePaymentStatuses(currentStatus)
          return validPaymentStatus.includes(value as EOperationStatus)
        },
      ),

    transactionDate: dateOnly()
      .when('newStatus', {
        is: (newStatus: EOperationStatus) => getStatusThatNeedsTransactionDate().has(newStatus),
        then: (schema) =>
          schema.required(() => t('loan.payment.changeStatusPayment.errors.transactionDateRequiredForStatus')),
        otherwise: (schema) => schema.nullable(),
      })
      .default(null),

    nsfStatus: mixed<EBankFailureCode>()
      .oneOf(getEBankFailureCodeList())
      .when('newStatus', {
        is: EOperationStatus.Failed,
        then: (schema) => schema.required(() => t('loan.payment.changeStatusPayment.errors.nsfStatusInvalid')),
        otherwise: (schema) => schema.nullable(),
      })
      .default(null),
    nsfFee: number()
      .nullable()
      .when('newStatus', {
        is: EOperationStatus.Failed,
        then: (schema) =>
          schema.required().min(0, () => t('loan.payment.changeStatusPayment.errors.nonNegativeNsfFee')),
        otherwise: (schema) => schema.nullable(),
      })
      .default(null),
  }),
)

export interface ChangePaymentStatusSchemaContext {
  simulation: CalendarSimulation
  currentStatus: EOperationStatus
}

export type ChangePaymentStatusSchemaType = InferType<ReturnType<typeof getChangePaymentStatusSchema>>

export const useChangePaymentStatusResolver = () => {
  return useMemo<Resolver<ChangePaymentStatusSchemaType, ChangePaymentStatusSchemaContext>>(
    () => yupResolver(getChangePaymentStatusSchema()),
    [],
  )
}
